import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import { resetDataComponent } from "../../StateManagement/actions";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { callAPI, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { UpdateStatus } from "../Reports/subComponents/UpdateStatus";
import WSFileUpload from "../../Components/FileUpload/WSFileUpload";
import http from "../../commonFunctions/httpClient";

const extractBackendErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  let message = fallback;

  if (typeof data === "string") {
    message = data;
  } else if (typeof data?.detail === "string") {
    message = data.detail;
  } else if (Array.isArray(data?.detail)) {
    message = data.detail[0]?.msg || fallback;
  } else if (data?.message) {
    message = data.message;
  } else if (error?.message) {
    message = error.message;
  }

  return message.length > 100 ? `${message.slice(0, 100)}...` : message;
};

// Mirrors ALLOWED_TRANSITIONS in the backend's service.py.
const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  Draft: ["Ordered", "Cancelled"],
  Ordered: ["Received", "Cancelled"],
  Received: ["Closed"],
  Closed: [],
  Cancelled: [],
};

// Delete only works on Draft or Cancelled (see delete_purchase_order_service).
const DELETABLE_STATUSES = ["Draft", "Cancelled"];

export const PurchaseOrderList: React.FC<any> = React.memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [isBillPopupOpen, setBillPopupOpen] = useState(false);
  const [billLoading, setBillLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: { newStatus: "" },
  });

  const {
    control: billControl,
    handleSubmit: handleBillSubmit,
    reset: resetBillForm,
  } = useForm<any>({
    defaultValues: { file: [] },
  });

  useEffect(() => {
    updateMainHeader("Sales");
    breadCrumbsStore.updateBreadCrumbs([{ "Purchase Order List": "/purchase-order/list" }]);
  }, []);

  const source = {
    url: "purchaseorder/list",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
    type: "GET" as const,
  };

  const columns: IColumns[] = [
    { header: "Date", fieldName: "CreatedDate", type: "date" },
    { header: "PO Number", fieldName: "PONumber" },
    { header: "Bill Number", fieldName: "BillNumber" },
    { header: "Bill Date", fieldName: "BillDate", type: "date" },
    { header: "Supplier", fieldName: "SupplierName" },
    { header: "Warehouse", fieldName: "WarehouseName" },
    { header: "Sub Total", fieldName: "SubTotal", type: "amount" },
    { header: "VAT", fieldName: "VatAmount", type: "amount" },
    { header: "Total (Incl. VAT)", fieldName: "TotalAmount", type: "amount" },
    // { header: "Remarks", fieldName: "Remarks" },
    { header: "Status", fieldName: "Status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete", "Update Status", "Upload Bill", "View Bill"],
      hideSelectedActions: {
        fieldToHide: [
          {
            field: "Status",
            hideValue: Object.keys(ALLOWED_TRANSITIONS).filter((status) => !DELETABLE_STATUSES.includes(status)),
            hideActions: ["Delete"],
          },
          {
            field: "Status",
            hideValue: Object.keys(ALLOWED_TRANSITIONS).filter((status) => ALLOWED_TRANSITIONS[status].length === 0),
            hideActions: ["Update Status"],
          },
        ],
      },
      actionCallback: (action: string, row: any) => {
        const purchaseOrderId = row.PurchaseOrderId || row.id;

        if (!purchaseOrderId) {
          console.error("No valid ID found in row:", row);
          showMessage("Invalid purchase order data - missing ID", "error");
          return;
        }

        if (action === "Edit") {
          navigate(`/purchase-order/${purchaseOrderId}`);
        } else if (action === "Delete") {
          setSelectedId(purchaseOrderId);
          setPopupOpen(true);
        } else if (action === "Update Status") {
          setSelectedId(purchaseOrderId);
          setSelectedStatus(row.Status || "");
          setStatusPopupOpen(true);
        } else if (action === "Upload Bill") {
          setSelectedId(purchaseOrderId);
          resetBillForm({ file: [] });
          setBillPopupOpen(true);
        } else if (action === "View Bill") {
          handleViewBill(purchaseOrderId);
        }
      },
    },
  ];

  const handleUpdateStatus = async (data: any) => {
    try {
      if (!selectedId) return;

      const response = await callAPI(`purchaseorder/status/${selectedId}`, Method.PUT, {
        Status: data.newStatus,
      });

      if (response?.success !== false) {
        showMessage("Purchase order status updated successfully!", "success");
        setStatusPopupOpen(false);
        reset();
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to update status", "error");
      }
    } catch (error: any) {
      console.error("Update Status Error:", error?.response?.data || error);
      showMessage(extractBackendErrorMessage(error, "Failed to update status"), "error");
    }
  };

  const handleUploadBill = async (data: any) => {
    if (!selectedId) return;
    if (!data.file || data.file.length === 0) {
      showMessage("Please select a bill file to upload", "error");
      return;
    }

    try {
      setBillLoading(true);
      const formData = new FormData();
      formData.append("id", String(selectedId));
      formData.append("file", data.file[0]);

      const response = await callAPI(
        `purchaseorder/${selectedId}/bill`,
        Method.POSTFORMDATA,
        undefined,
        undefined,
        formData
      );

      if (response?.success !== false) {
        showMessage("Bill uploaded successfully!", "success");
        setBillPopupOpen(false);
        resetBillForm({ file: [] });
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to upload bill", "error");
      }
    } catch (error: any) {
      console.error("Upload Bill Error:", error?.response?.data || error);
      showMessage(extractBackendErrorMessage(error, "Failed to upload bill"), "error");
    } finally {
      setBillLoading(false);
    }
  };

  const handleViewBill = async (purchaseOrderId: string | number) => {
    try {
      const result: any = await http.get(`purchaseorder/${purchaseOrderId}/bill`, {
        responseType: "blob",
      });

      if (!(result instanceof Blob)) {
        showMessage("Failed to load bill", "error");
        return;
      }

      if (result.type && result.type.includes("json")) {
        let message = "No bill uploaded for this purchase order";
        try {
          const text = await result.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || parsed?.detail || message;
        } catch {
          // keep default message
        }
        showMessage(message, "error");
        return;
      }

      const blobUrl = URL.createObjectURL(result);
      window.open(blobUrl, "_blank");
    } catch (error: any) {
      console.error("View Bill Error:", error);
      showMessage("Failed to load bill", "error");
    }
  };

  const handleClick = () => {
    navigate("/purchase-order");
  };

  return (
    <Grid p={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <WSDataGrid
            id="purchaseOrderGrid"
            key={refreshKey}
            title="Purchase Order List"
            headerType="modern"
            columns={columns}
            source={source}
            rowsPerPageCount={10}
            searchBox={{ searchMode: "query" }}
            addButton={{ label: "Add New Purchase Order", color: "secondary" }}
            onAddButtonClick={handleClick}
            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>

        <ConfirmationPopup
          type="DELETE"
          apiUrl="purchaseorder"
          id={selectedId}
          confirmMessage="Are you sure you want to delete this purchase order?"
          open={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onConfirm={(data: any) => {
            dispatch(resetDataComponent("purchaseOrderGrid"));

            if (data?.success) {
              showMessage(data.message || "Purchase order deleted successfully!", "success");
              return;
            }

            let message = data?.detail || data?.message || "Delete failed";
            if (typeof message === "string" && message.length > 100) {
              message = `${message.slice(0, 100)}...`;
            }
            showMessage(message, "error");
          }}
        />

        <Dialog open={isStatusPopupOpen} onClose={() => setStatusPopupOpen(false)}>
          <DialogTitle>Update Purchase Order Status</DialogTitle>
          <DialogContent>
            <UpdateStatus
              control={control}
              errors={{}}
              register={register}
              id={selectedId}
              reset={reset}
              currentStatus={selectedStatus}
              config={(ALLOWED_TRANSITIONS[selectedStatus] || []).map((status) => ({
                value: status,
                label: status,
              }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusPopupOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleUpdateStatus)}
              disabled={!isValid}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isBillPopupOpen} onClose={() => setBillPopupOpen(false)}>
          <DialogTitle>Upload Purchase Order Bill</DialogTitle>
          <DialogContent sx={{ minWidth: 360 }}>
            <WSFileUpload
              id="bill-file"
              name="file"
              label="Bill File"
              control={billControl}
              acceptFileTypes=".pdf,.jpg,.jpeg,.png"
              maxFileSizeMB={5}
              multiple={false}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBillPopupOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBillSubmit(handleUploadBill)}
              disabled={billLoading}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
});

export default PurchaseOrderList;
